import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common'
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core'
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { Observable, Subject } from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
} from 'rxjs/operators'
import { InfiniteScrollContainerComponent } from '@wm-front/shared/infinite-scroll'
import { InputComponent } from '@wm-front/shared/forms'
import { HighlightSearchPipe } from '@wm-front/shared/pipes'
import { latinise } from '@wm-front/shared/utils'
import { DropdownOption } from '@wm-front/core'
import { MatRadioModule } from '@angular/material/radio'
import { NgClickOutsideDelayOutsideDirective } from 'ng-click-outside2'

@Component({
  selector: 'wm-front-autocomplete-dropdown',
  templateUrl: './autocomplete-dropdown.component.html',
  styleUrls: ['./autocomplete-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgClass,
    MatIconModule,
    MatCheckboxModule,
    FormsModule,
    ReactiveFormsModule,
    InputComponent,
    InfiniteScrollContainerComponent,
    MatProgressSpinnerModule,
    HighlightSearchPipe,
    AsyncPipe,
    MatIconModule,
    NgTemplateOutlet,
    MatRadioModule,
    NgClickOutsideDelayOutsideDirective,
  ],
})
export class AutocompleteDropdownComponent implements OnInit, OnDestroy {
  /** Whether or not query is flying */
  @Input() public loading: boolean

  /** Whether or there is a server error */
  @Input() public error: boolean

  /** The placeholder to display in the autocomplete input */
  @Input() public placeholder?: string

  /** Should you want to hide the search bar */
  @Input() public hideSearch = false

  /** Use full width container */
  @Input() public isFullWidth = false

  /** Override the activation of reset button */
  @Input() public activeResetBtn = false

  /** Should you want to hide reset button */
  @Input() public hideResetBtn = false

  /** Should you want a border and shadow stripped button, this is the property to use */
  @Input() public flat = false

  /** Flag that the filtering is done by the backend */
  @Input() public backendFiltering = false

  /** Should you want a default unclickable option when a search doesn't match anything: here is the place to do it */
  @Input() public emptySearchMessage?: string

  /** If we want to display radio mode instead of checkbox */
  @Input() public radioMode = false

  @Input() public disabled = false

  /** Minimum number of characters typed in the input before queryChanged is triggered */
  @Input() public minSearchLength = 2

  /** Event that triggers when at least minSearchLength characters are typed in the input field */
  @Output() public queryChanged: EventEmitter<string> = new EventEmitter()

  /** Event that triggers when a value is picked in the dropdown */
  @Output()
  public valuePicked: EventEmitter<DropdownOption> = new EventEmitter()

  /** Event that triggers when we want to close the dropdown  */
  @Output() public shouldClose: EventEmitter<boolean> = new EventEmitter()

  /** Event that triggers when the options checked needs to be resetted */
  @Output() public reseted: EventEmitter<void> = new EventEmitter()

  /** Event that triggers when the options checked needs to be resetted */
  @Output() public scrolled: EventEmitter<void> = new EventEmitter()

  /** The filtered array of options */
  public filteredOptions$: Observable<DropdownOption[]>

  /** The selected array of options */
  public selectedOptions: DropdownOption[]

  /** The form that holds the field of autocomplete */
  public autocompleteForm: UntypedFormGroup

  /** The private property stocking the array of data to filter */
  private _options: DropdownOption[]

  /** The query subject that hold the changes of the input value */
  private querySubject: Subject<string> = new Subject<string>()

  /** Keep the original position in the options list for this values */
  private holdOptions: (string | number | boolean)[] = []

  /** Checked options at the init, need to be at the top of the list */
  private checkedOptions: (string | number | boolean)[] = []

  /** The setter of array of data to filter */
  @Input() public set options(options: DropdownOption[]) {
    const optionWithoutGroup = options.reduce((acc, curr) => {
      if (curr.children) {
        curr.children.forEach((option) => {
          acc.push(option)
        })
      } else {
        acc.push(curr)
      }

      return acc
    }, [] as DropdownOption[])

    const parentOptions = options.map((option) => {
      const parentOption = { ...option }
      if (option.children?.every((child) => child.checked)) {
        parentOption.checked = true
      } else if (option.children?.some((child) => child.checked)) {
        parentOption.isIndeterminate = true
      }
      return parentOption
    })

    this._options = parentOptions
    this.selectedOptions = optionWithoutGroup.filter((option) => option.checked)

    if (this.autocompleteForm) {
      this.listenForQuery(this.autocompleteForm.controls['searchQuery'].value)
    }
  }

  /** Closes the dropdown on escape key */
  @HostListener('keydown', ['$event']) public triggerEsc(
    e: KeyboardEvent
  ): void {
    if (e.key === 'Escape') {
      this.shouldClose.emit(true)
      this.clearHoldOptions()
    }
  }

  /** Closes the dropdown when we click outside of it */
  public onClickedOutside(): void {
    this.shouldClose.emit(true)
    this.clearHoldOptions()
  }

  public ngOnInit(): void {
    this.createAutocompleteForm()
    this.listenForQuery()

    this.checkedOptions = this._options
      .filter((option) => option.checked)
      .map((option) => option.value)

    this.querySubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((query) => {
        this.queryChanged.emit(query)
      })
  }

  public ngOnDestroy(): void {
    this.querySubject.unsubscribe()
  }

  public pickParent(option: DropdownOption): void {
    option.children?.forEach((child) =>
      this.pickValue(child, {
        onlySelect: !option.checked || !!option.isIndeterminate,
        onlyDeselect: !!option.checked,
      })
    )
  }

  /** Used to pick a value from the dropdown
   * @param { DropdownOption } option the option picked from the checkbox
   * @param multiOption
   */
  public pickValue(
    option: DropdownOption,
    multiOption?: { onlySelect: boolean; onlyDeselect: boolean }
  ): void {
    if (!multiOption) {
      const valueIsAlreadyThere = this.holdOptions.includes(option.value)
      if (!valueIsAlreadyThere) {
        this.holdOptions.push(option.value)
      }
      setTimeout(() => {
        this.valuePicked.emit(option)
      })
    } else {
      if (option.checked && multiOption.onlyDeselect) {
        setTimeout(() => {
          this.valuePicked.emit(option)
        })
      } else if (!option.checked && multiOption.onlySelect) {
        this.holdOptions.push(option.value)
        setTimeout(() => {
          this.valuePicked.emit(option)
        })
      }
    }
  }

  /** Handles the change of query in the input
   * @param { InputEvent } event the event emitted from the input event
   */
  public handleQueryChanged(event: InputEvent): void {
    const value = (event.target as any).value

    if (value.length >= this.minSearchLength) {
      this.querySubject.next(value)
    } else if (value.length === 0) {
      this.querySubject.next('')
    }
  }

  /** Resets the options selected, sends an event to the parent component so the options from the store can be resetted */
  public resetSelectedOptions(): void {
    if (this.selectedOptions.length || this.activeResetBtn) {
      this.reseted.emit()
    }
  }

  /** Trackby method to prevent recreation of items
   * @param { number }
   * @param { DropdownOption }
   * @returns { string }
   */
  public trackByFn(i: number, item: DropdownOption): string {
    return item.value as string
  }

  /** Creates all the fields required in order to validate tha autocomplete field */
  private createAutocompleteForm(): void {
    this.autocompleteForm = new UntypedFormGroup({
      searchQuery: new UntypedFormControl(),
    })
  }

  /** We watch the value change of the form control and we update the options accordingly
   * @param {string} query Use to init with a value, useful to not lose the current search if refreshed
   */
  private listenForQuery(query = ''): void {
    this.filteredOptions$ = this.autocompleteForm.controls[
      'searchQuery'
    ].valueChanges.pipe(
      startWith(query),
      map((value): DropdownOption[] => {
        return this.backendFiltering
          ? this._options.map((option: DropdownOption) => option)
          : this.filter(this._options, value).sort((a, b) =>
              this.reorderOptions(a, b)
            )
      })
    )
  }

  /** Used to filter the array of options with the term we receive from the input
   * @param {string} value the value we receive as string
   * @return {Array<DropdownOption>}
   */
  private filter(options: DropdownOption[], value: string): DropdownOption[] {
    let filterValue = ''

    if (value) {
      filterValue = latinise(value.toLowerCase())
    }

    return options?.filter((option: DropdownOption): boolean =>
      latinise(option.label.toLowerCase()).includes(filterValue)
    )
  }

  /** clear the hold options array */
  private clearHoldOptions(): void {
    this.holdOptions = []
  }

  /** Used to sort the options for placed the checked options at the top  */
  private reorderOptions(a: DropdownOption, b: DropdownOption): number {
    if (
      (a.checked && !b.checked && !this.holdOptions.includes(a.value)) ||
      this.checkedOptions.includes(a.value)
    ) {
      return -1
    }

    return 0
  }
}
